import * as types from '../types';
import { isModuleSubscriptionProduct, isUltraProduct } from '../types/Product';
import { Species } from '../types/gql-op-types';
import { FiCountry } from '../contexts/LocaleContext';
import { isAxiosError, webApiClient } from './fi-api/apiUtils';
import { logInternalError } from './errors';
import { getProductsBySku } from './product';

export const COLLAR_TARIFF_FEE_SKU = 'collar-tariff-50';

export function cartHasSomePhysicalProducts(cart: types.Cart, products: types.IProduct[]) {
  const productMap = getProductsBySku(products);
  return Object.values(cart.cartItems).some((cartItem) => {
    const product = productMap.get(cartItem.lineItem.sku);
    return product && product.physical;
  });
}

export function isBundleCartItem(cartItem: types.CartItem): cartItem is types.BundleCartItem {
  return 'subscriptionLineItem' in cartItem;
}

/**
 * Sums only the credits the coupon itself created.
 *
 * `getCartTestTotalDiscount` is the wrong source when the question is "what did this promo do":
 * it folds in gift cards, account balance and multi-dog waivers, so a stacked cart would credit
 * the promo with money it did not discount. Shared so the promo confirmation row and the promo
 * totals line always quote the same figure.
 */
export function appliedCouponDiscountInCents(cartPricing: types.CartPricing): number {
  return Object.values(cartPricing.cartItemCredits || {}).reduce(
    (total, credits) =>
      total +
      credits.reduce(
        (creditTotal, credit) =>
          credit.creditReason === 'promo' || credit.creditReason === 'promo_waived_activation_fee'
            ? creditTotal + credit.creditAmountInCents
            : creditTotal,
        0,
      ),
    0,
  );
}

/**
 * Returns true when every bundle in the cart has cat species selected.
 * Non-bundle items (gift cards, supplements, etc.) are not considered.
 * Used to skip the supplements upsell — supplements are dog-only products.
 */
export function allBundlesAreCatSpecies(cart: types.Cart): boolean {
  const bundles = Object.values(cart.cartItems).filter(isBundleCartItem);
  if (bundles.length === 0) return false;
  return bundles.every((item) => item.species === Species.CAT);
}

export function isGiftCardCartItem(cartItem: types.CartItem): cartItem is types.GiftCardCartItem {
  return 'giftCardDelivery' in cartItem;
}

export function isForModuleCartItem(
  cartItem: types.CartItem,
): cartItem is types.ForModuleBundleCartItem | types.ForModuleCartItem {
  return 'forModuleId' in cartItem;
}

export function isUpgradeCartItem(cartItem: types.CartItem): cartItem is types.Series3UpgradeCartItem {
  return isBundleCartItem(cartItem) && isForModuleCartItem(cartItem);
}

export function skusForCartItem(cartItem: types.CartItem) {
  if (isBundleCartItem(cartItem)) {
    const addonSkus = cartItem.addonLineItems?.map((lineItem) => lineItem.sku) ?? [];
    return [cartItem.lineItem.sku, cartItem.subscriptionLineItem.sku, ...addonSkus];
  }

  return [cartItem.lineItem.sku];
}

export function giftCardCartItems(cart: types.Cart) {
  return Object.values(cart.cartItems).filter(isGiftCardCartItem);
}

export function cartHasItems(cart: types.Cart) {
  return Object.keys(cart.cartItems).length > 0;
}

/**
 * Shared predicate: returns [id, cartItem] entries for bundles with a free trial subscription.
 * Used by the free-trial helpers below to avoid duplicating the filter logic.
 */
function freeTrialBundleEntries(cart: types.Cart, products: types.IProduct[]) {
  const productMap = getProductsBySku(products);
  return Object.entries(cart.cartItems).filter(([, item]) => {
    if (!isBundleCartItem(item)) return false;
    const sub = productMap.get(item.subscriptionLineItem.sku);
    return sub && isModuleSubscriptionProduct(sub) && sub.freeTrial;
  });
}

export function cartContainsOnlyFreeTrialBundles(cart: types.Cart, products: types.IProduct[]): boolean {
  const bundles = Object.values(cart.cartItems).filter(isBundleCartItem);
  if (bundles.length === 0) return false;
  return freeTrialBundleEntries(cart, products).length === bundles.length;
}

export function getFreeTrialCartItemIds(cart: types.Cart, products: types.IProduct[]): string[] {
  return freeTrialBundleEntries(cart, products).map(([id]) => id);
}

/** A bundle charged for its membership today, e.g. a partner code's 6-month plan plus its activation fee. */
export function cartContainsPaidBundle(cart: types.Cart, products: types.IProduct[]): boolean {
  const bundleCount = Object.values(cart.cartItems).filter(isBundleCartItem).length;
  return bundleCount > freeTrialBundleEntries(cart, products).length;
}

export function cartContainsAnyFreeTrialBundle(cart: types.Cart, products: types.IProduct[]): boolean {
  return freeTrialBundleEntries(cart, products).length > 0;
}

/**
 * Returns true when the cart contains at least one Fi Ultra product, whether purchased as a
 * standalone hardware line item or bundled with a subscription.
 * Used to skip the post-checkout supplements upsell for Ultra purchases.
 */
export function cartContainsUltra(cart: types.Cart, products: types.IProduct[]): boolean {
  const productMap = getProductsBySku(products);
  return Object.values(cart.cartItems).some((item) =>
    skusForCartItem(item).some((sku) => {
      const product = productMap.get(sku);
      return product ? isUltraProduct(product) : false;
    }),
  );
}

/**
 * Validates whether items in the cart are available for purchase in the
 * given country. Returns IDs of invalid cart items so the caller can
 * remove them and notify the user.
 */
export async function validateCart(cart: types.Cart, country: FiCountry): Promise<string[]> {
  try {
    await webApiClient.post('/api/ecommerce/validate-cart', {
      cartItems: Object.values(cart.cartItems),
      countryCode: country,
    });
    return [];
  } catch (err) {
    if (isAxiosError(err) && err.response?.data?.error?.cartItemIds) {
      return err.response.data.error.cartItemIds;
    }
    logInternalError(err, { tags: { source: 'validateCart' } });
    throw err;
  }
}

/**
 * A line's unit price after its coupon saving, as Recurly gives it and the bag shows it (`cartItemCredits`), not the
 * coupon's percent: a code only discounts the lines it is eligible for, and a dollar-off code has no percent. Equal to
 * the unit price when the line has no promo credit. Undefined for a credited bundle, whose credit may sit on its
 * membership rather than this line.
 */
export function promoUnitPriceInCents(
  cartItem: types.CartItem,
  unitPriceInCents: number,
  cartPricing: types.CartPricing,
): number | undefined {
  const promoCreditInCents = (cartPricing.cartItemCredits?.[cartItem.cartItemId] ?? [])
    .filter((credit) => credit.creditReason === 'promo')
    .reduce((sum, credit) => sum + credit.creditAmountInCents, 0);
  if (promoCreditInCents === 0) {
    return unitPriceInCents;
  }
  if (isBundleCartItem(cartItem)) {
    return undefined;
  }
  // the credit covers the whole line, every unit of it
  return Math.max(0, unitPriceInCents - Math.round(promoCreditInCents / cartItem.quantity));
}
